/*
 * validation.js
 * -------------
 * One validator per step. Each validator returns
 *
 *   { errors: { [fieldId]: message }, hasErrors: boolean, summary: [..] }
 *
 * Field ids are strings used both to look up the error in the rendered step
 * (so each field can show a red border + message) and as DOM `data-field-id`
 * attributes so we can scroll to the first error on Continue.
 *
 * Conventions:
 *   - general.<field>
 *   - address.current.<field>           address.previous.<idx>.<field>
 *   - employment.current.<field>        employment.previous.<idx>.<field>
 *   - expenses.<field>
 *   - liabilities.loans.<id>.<field>    liabilities.credit_cards.<id>.<field>
 *   - assets.<collection>.<key>.<field>
 *   - loan_req.<field>                  loan_req.important_features.<key>
 */

import { parseStepId } from './steps.config';
import { coverageAnalysis } from './dateUtils';
import { isAUAddress } from './addressRules';
import { EXPENSE_CATEGORIES } from './steps/StepLivingExpenses';

const isBlank = (v) => v == null || String(v).trim() === '';

const isDateBlank = (d) =>
  !d || isBlank(d.day) || isBlank(d.month) || isBlank(d.year);

const required = (errors, fieldId, value, message) => {
  if (isBlank(value)) errors[fieldId] = message || 'This field is required.';
};

const requiredDate = (errors, fieldId, value, message) => {
  if (isDateBlank(value)) errors[fieldId] = message || 'Required - DD / MM / YYYY.';
};

const buildResult = (errors) => ({
  errors,
  hasErrors: Object.keys(errors).length > 0,
});

const validateGeneralDetails = (applicant) => {
  const errors = {};
  if (!applicant) return buildResult(errors);
  required(errors, 'general.title', applicant.title, 'Choose a title.');
  required(errors, 'general.gender', applicant.gender, 'Choose a gender.');
  required(errors, 'general.given_name', applicant.given_name, 'Given name is required.');
  required(errors, 'general.family_name', applicant.family_name, 'Family name is required.');
  required(errors, 'general.email', applicant.email, 'Email is required.');
  required(errors, 'general.mobile', applicant.mobile, 'Mobile number is required.');
  requiredDate(errors, 'general.dob', applicant.dob, 'Date of birth is required.');
  required(errors, 'general.martial_status', applicant.martial_status, 'Choose a marital status.');
  required(errors, 'general.resident_status', applicant.resident_status, 'Choose your residency status.');
  if (applicant.resident_status === 'Visa') {
    required(errors, 'general.resident_visa_details', applicant.resident_visa_details, 'Tell us which visa you hold.');
  }
  (applicant.dependants || []).forEach((d, idx) => {
    if (isBlank(d.name)) errors[`general.dependants.${idx}.name`] = 'Name is required.';
    if (isBlank(d.age)) errors[`general.dependants.${idx}.age`] = 'Age is required.';
  });
  return buildResult(errors);
};

const validateAddressFields = (errors, prefix, address, includeMovedOut) => {
  required(errors, `${prefix}.country`, address.country, 'Country is required.');
  if (isAUAddress(address.country)) {
    required(errors, `${prefix}.street_number`, address.street_number, 'Street number is required.');
    required(errors, `${prefix}.street_name`, address.street_name, 'Street name is required.');
    required(errors, `${prefix}.state`, address.state, 'State is required.');
    required(errors, `${prefix}.postcode`, address.postcode, 'Postcode is required.');
  } else {
    // Overseas addresses only capture what Google Places gives back. Street
    // numbering, states and postcodes are not universal, so the picked
    // address is the piece we require.
    required(
      errors,
      `${prefix}.address_search`,
      address.address_search || address.street,
      'Search for the address and pick it from the list.'
    );
  }
  required(errors, `${prefix}.suburb`, address.suburb, 'Suburb / city is required.');
  required(errors, `${prefix}.address_type`, address.address_type, 'Choose a living arrangement.');
  if (['Renting', 'Boarding'].includes(address.address_type)) {
    required(errors, `${prefix}.address_amount`, address.address_amount, 'Rent / board amount is required.');
  }
  if (address.address_type === 'Other') {
    required(errors, `${prefix}.other_text`, address.other_text, 'Please describe.');
  }
  requiredDate(errors, `${prefix}.moved_in`, address.moved_in, 'Move-in date is required.');
  if (includeMovedOut) {
    requiredDate(errors, `${prefix}.moved_out`, address.moved_out, 'Move-out date is required.');
  }
};

const validateAddressHistory = (applicant) => {
  const errors = {};
  if (!applicant) return buildResult(errors);
  const address = applicant.address || { current: {}, previous: [] };
  validateAddressFields(errors, 'address.current', address.current || {}, false);
  (address.previous || []).forEach((p, idx) => {
    validateAddressFields(errors, `address.previous.${idx}`, p, true);
  });

  // 3-year coverage check - both total months AND continuity.
  const entries = [];
  if (address.current && address.current.moved_in) {
    entries.push({ startDate: address.current.moved_in, endDate: null });
  }
  (address.previous || []).forEach((p) => {
    if (p.moved_in) entries.push({ startDate: p.moved_in, endDate: p.moved_out });
  });
  const analysis = coverageAnalysis(entries);
  if (analysis.hasGap) {
    const firstGap = analysis.gaps[0];
    errors['address.coverage'] =
      `There's a gap in your address timeline (${firstGap.from} - ${firstGap.to}). ` +
      'We need 3 years of continuous history with no missing periods.';
  } else if (analysis.months < 36) {
    errors['address.coverage'] =
      `You\'ve provided ${Math.floor(analysis.months / 12)}y ${analysis.months % 12}m of history. ` +
      'Add previous addresses until you have at least 3 years.';
  }
  return buildResult(errors);
};

const validateEmploymentFields = (errors, prefix, job, includeEndDate) => {
  required(errors, `${prefix}.position`, job.position, 'Position is required.');
  required(errors, `${prefix}.employer_name`, job.employer_name, 'Employer name is required.');
  required(errors, `${prefix}.employer_phone`, job.employer_phone, 'Employer phone is required.');
  required(errors, `${prefix}.employer_address`, job.employer_address, 'Employer address is required.');
  required(errors, `${prefix}.gross_income`, job.gross_income, 'Gross income is required.');
  required(errors, `${prefix}.employment_status`, job.employment_status, 'Choose an employment status.');
  requiredDate(errors, `${prefix}.start_date`, job.start_date, 'Start date is required.');
  if (includeEndDate) {
    requiredDate(errors, `${prefix}.end_date`, job.end_date, 'End date is required.');
  }
};

const validateEmployment = (applicant) => {
  const errors = {};
  if (!applicant) return buildResult(errors);
  const employment = applicant.employment || { current: {}, previous: [] };
  validateEmploymentFields(errors, 'employment.current', employment.current || {}, false);
  (employment.previous || []).forEach((p, idx) => {
    validateEmploymentFields(errors, `employment.previous.${idx}`, p, true);
  });

  // 3-year coverage check - both total months AND continuity.
  const entries = [];
  if (employment.current && employment.current.start_date) {
    entries.push({ startDate: employment.current.start_date, endDate: null });
  }
  (employment.previous || []).forEach((p) => {
    if (p.start_date) entries.push({ startDate: p.start_date, endDate: p.end_date });
  });
  const analysis = coverageAnalysis(entries);
  if (analysis.hasGap) {
    const firstGap = analysis.gaps[0];
    errors['employment.coverage'] =
      `There's a gap in your employment timeline (${firstGap.from} - ${firstGap.to}). ` +
      'We need 3 years of continuous history with no missing periods.';
  } else if (analysis.months < 36) {
    errors['employment.coverage'] =
      `You\'ve provided ${Math.floor(analysis.months / 12)}y ${analysis.months % 12}m of employment history. ` +
      'Add previous roles until you have at least 3 years.';
  }
  return buildResult(errors);
};

const validateLivingExpenses = (expenses) => {
  const errors = {};
  EXPENSE_CATEGORIES.forEach((cat) => {
    if (!cat.optional) {
      required(errors, `expenses.${cat.key}`, expenses[cat.key], 'This expense is required.');
    }
  });
  return buildResult(errors);
};

const ownershipOk = (ownership, heldOtherText) =>
  Object.values(ownership || {}).some(Boolean) || !isBlank(heldOtherText);

const requireOwnership = (errors, fieldId, ownership, heldOtherText) => {
  if (!ownershipOk(ownership, heldOtherText)) {
    errors[fieldId] = 'Pick at least one owner (or fill in Other).';
  }
};

const validateLiabilities = (liabilities) => {
  const errors = {};
  Object.values(liabilities.loans || {}).forEach((loan) => {
    required(errors, `liabilities.loans.${loan.id}.bank_name`, loan.bank_name, 'Lender / bank is required.');
    required(errors, `liabilities.loans.${loan.id}.owing`, loan.owing, 'Amount owing is required.');
    required(errors, `liabilities.loans.${loan.id}.repayment`, loan.repayment, 'Monthly repayment is required.');
    requireOwnership(
      errors,
      `liabilities.loans.${loan.id}.ownership`,
      loan.ownership,
      loan.held_other_text
    );
  });
  Object.values(liabilities.credit_cards || {}).forEach((card) => {
    required(errors, `liabilities.credit_cards.${card.id}.bank_name`, card.bank_name, 'Provider is required.');
    required(errors, `liabilities.credit_cards.${card.id}.limit`, card.limit, 'Credit limit is required.');
    requireOwnership(
      errors,
      `liabilities.credit_cards.${card.id}.ownership`,
      card.ownership,
      card.held_other_text
    );
  });
  Object.values(liabilities.leases || {}).forEach((lease) => {
    required(errors, `liabilities.leases.${lease.id}.lessor`, lease.lessor, 'Lessor / provider is required.');
    required(errors, `liabilities.leases.${lease.id}.lease_type`, lease.lease_type, 'Choose a lease type.');
    required(errors, `liabilities.leases.${lease.id}.monthly_payment`, lease.monthly_payment, 'Monthly payment is required.');
    requireOwnership(
      errors,
      `liabilities.leases.${lease.id}.ownership`,
      lease.ownership,
      lease.held_other_text
    );
  });
  return buildResult(errors);
};

const validateAssets = (assets) => {
  const errors = {};

  // Properties
  Object.keys(assets.properties || {}).forEach((key) => {
    const p = assets.properties[key];
    required(errors, `assets.properties.${key}.country`, p.country, 'Country is required.');
    if (isAUAddress(p.country)) {
      required(errors, `assets.properties.${key}.street_number`, p.street_number, 'Street number is required.');
      required(errors, `assets.properties.${key}.street_name`, p.street_name, 'Street name is required.');
      required(errors, `assets.properties.${key}.state`, p.state, 'State is required.');
      required(errors, `assets.properties.${key}.postcode`, p.postcode, 'Postcode is required.');
    } else {
      required(
        errors,
        `assets.properties.${key}.address_search`,
        p.address_search || p.address,
        'Search for the address and pick it from the list.'
      );
    }
    required(errors, `assets.properties.${key}.suburb`, p.suburb, 'Suburb / city is required.');
    required(errors, `assets.properties.${key}.property_use`, p.property_use, 'Choose owner occupier or investment.');
    required(errors, `assets.properties.${key}.value`, p.value, 'Estimated value is required.');
    if (p.property_use === 'Investment') {
      required(
        errors,
        `assets.properties.${key}.rental_income`,
        p.rental_income,
        'Rental income is required for investment properties.'
      );
    }
    requireOwnership(
      errors,
      `assets.properties.${key}.ownership`,
      p.ownership,
      p.held_other_text
    );
  });

  // Home contents value is mandatory.
  required(errors, 'assets.home_contents_value', assets.home_contents_value, 'Home contents value is required.');

  Object.keys(assets.savings || {}).forEach((key) => {
    const s = assets.savings[key];
    required(errors, `assets.savings.${key}.name`, s.name, 'Account / institution is required.');
    required(errors, `assets.savings.${key}.value`, s.value, 'Current balance is required.');
    requireOwnership(
      errors,
      `assets.savings.${key}.ownership`,
      s.ownership,
      s.held_other_text
    );
  });
  Object.keys(assets.shares || {}).forEach((key) => {
    const s = assets.shares[key];
    required(errors, `assets.shares.${key}.name`, s.name, 'Share / fund name is required.');
    required(errors, `assets.shares.${key}.value`, s.value, 'Current value is required.');
    requireOwnership(
      errors,
      `assets.shares.${key}.ownership`,
      s.ownership,
      s.held_other_text
    );
  });
  Object.keys(assets.supers || {}).forEach((key) => {
    const s = assets.supers[key];
    required(errors, `assets.supers.${key}.name`, s.name, 'Fund name is required.');
    required(errors, `assets.supers.${key}.value`, s.value, 'Current balance is required.');
    requireOwnership(
      errors,
      `assets.supers.${key}.ownership`,
      s.ownership,
      s.held_other_text
    );
  });
  Object.keys(assets.vehicles || {}).forEach((key) => {
    const v = assets.vehicles[key];
    required(errors, `assets.vehicles.${key}.make`, v.make, 'Make is required.');
    required(errors, `assets.vehicles.${key}.model`, v.model, 'Model is required.');
    required(errors, `assets.vehicles.${key}.value`, v.value, 'Estimated value is required.');
    requireOwnership(
      errors,
      `assets.vehicles.${key}.ownership`,
      v.ownership,
      v.held_other_text
    );
  });
  Object.keys(assets.other_assets || {}).forEach((key) => {
    const o = assets.other_assets[key];
    required(errors, `assets.other_assets.${key}.name`, o.name, 'Asset name is required.');
    required(errors, `assets.other_assets.${key}.value`, o.value, 'Estimated value is required.');
    requireOwnership(
      errors,
      `assets.other_assets.${key}.ownership`,
      o.ownership,
      o.held_other_text
    );
  });

  return buildResult(errors);
};

const validateLoanRequirements = (lr) => {
  const errors = {};
  const whyChecked = Object.values(lr.why || {}).some(Boolean);
  if (!whyChecked) errors['loan_req.why'] = 'Pick at least one reason.';
  const assistChecked = Object.values(lr.what_can_assist || {}).some(Boolean);
  if (!assistChecked) errors['loan_req.what_can_assist'] = 'Pick at least one option.';

  required(errors, 'loan_req.loan_term', lr.loan_term, 'Choose a loan term.');
  if (lr.loan_term === 'other') {
    required(errors, 'loan_req.loan_term_other', lr.loan_term_other, 'Enter the term in years.');
  }

  required(errors, 'loan_req.loan_repayment_type', lr.loan_repayment_type, 'Choose a repayment type.');
  if (lr.loan_repayment_type === 'Interest Only') {
    required(errors, 'loan_req.interest_only_term', lr.interest_only_term, 'Choose an interest only term.');
  }

  required(errors, 'loan_req.rate_type', lr.rate_type, 'Choose a rate type.');

  const features = lr.important_features || {};
  ['basic_home_loan', 'offset_account', 'redraw_facility', 'professional_packages', 'extra_repayments_allowed'].forEach((key) => {
    const f = features[key] || {};
    if (isBlank(f.answer)) {
      errors[`loan_req.important_features.${key}`] = 'Choose Yes, Nice to have or No.';
    }
  });

  return buildResult(errors);
};

export const validateStep = (stepId, state) => {
  const { kind, applicantId } = parseStepId(stepId);
  const applicant = applicantId
    ? (state.applicants || []).find((a) => a.id === applicantId)
    : null;

  switch (kind) {
    case 'General_Details':
      return validateGeneralDetails(applicant);
    case 'Address_History':
      return validateAddressHistory(applicant);
    case 'Employment':
      return validateEmployment(applicant);
    case 'Living_Expenses':
      return validateLivingExpenses(state.expenses || {});
    case 'Liabilities':
      return validateLiabilities(state.liabilities || {});
    case 'Assets':
      return validateAssets(state.assets || {});
    case 'Loan_Requirements':
      return validateLoanRequirements(state.loanRequirements || {});
    case 'Review':
    default:
      return buildResult({});
  }
};

// Scroll the first errored DOM element into view. Field components mark
// themselves with data-field-id so we can find them.
export const scrollToFirstError = (errors) => {
  if (typeof document === 'undefined') return;
  const ids = Object.keys(errors);
  for (const id of ids) {
    const el = document.querySelector(`[data-field-id="${CSS.escape(id)}"]`);
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const focusable = el.querySelector(
        'input, select, textarea, button'
      );
      if (focusable && focusable.focus) {
        setTimeout(() => focusable.focus({ preventScroll: true }), 250);
      }
      return;
    }
  }
};
